
import React from 'react';
import { Row, Col, Form, Button, InputGroup, Alert, Modal } from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import { IMaskInput } from 'react-imask';
import { getDataAtual } from '../../common/utils';
import {
  FaClock,
  FaExclamationTriangle,
  FaExclamation,
  FaCheckCircle,
  FaPhone
} from 'react-icons/fa';

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import 'bootstrap-icons/font/bootstrap-icons.css';

/**
 * Componente genérico para renderizar campos dinâmicos de formulário,
 * incluindo suporte a máscara via IMaskInput (react-imask).
 * Props:
 * - campos: array de configuração de campos
 * - valoresCampos: object { campoId: valor }
 * - camposOutro: object para armazenar texto de "Outro"
 * - onChangeCampo(id, valor ou event)
 * - onChangeCampoAsync
 * - onChangeCampoCheckSim
 * - onChangeOutro
 * - dadosUsuario
 * - opcaoModalRelato
 * - tiposSessoesCampos
 * - carregarProdutosPrograma
 * - gotoConfig: config de gotoConnect
 */
export default function FormularioRender({
  campos,
  valoresCampos = [],
  camposOutro,
  maxLengthOverrides = {},
  onChangeCampo,
  onChangeCampoAsync,
  onChangeCampoCheckSim,
  onChangeOutro,
  dadosUsuario,
  opcaoModalRelato,
  tiposSessoesCampos,
  carregarProdutosPrograma,
  gotoConfig = {},
  doencaId,
  dados_beneficiario = [],
  dadosMedico = [],
  camposExtraCadastrados = [],
  programaDados = [],
  produtosOpcoes = [],
  produtoNome = '',
  abrirModalHistoricoLigacoes
}) {

  const UUID_RE = /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/;
  const HIP_RE  = /^\d{8}-\d{2}$/;

  // slugs podem vir com sufixo numérico quando o campo se repete em vários formulários
  // (ex.: "gotoconnect-id-9", "protocolo-hiplatform-3"), por isso o match é por prefixo.
  // A ordem das palavras também varia entre formulários ("hiplatform-protocolo" vs "protocolo-hiplatform").
  const isHiplatformProtocoloSlug = (slug = '') =>
    slug === 'hiplatform-protocolo' || slug.startsWith('hiplatform-protocolo-') ||
    slug === 'protocolo-hiplatform' || slug.startsWith('protocolo-hiplatform-');
  const isGotoConnectIdSlug = (slug = '') =>
    slug === 'gotoconnect-id' || slug.startsWith('gotoconnect-id-');

  const isMaskedBySlug = (slug = '') =>
    isHiplatformProtocoloSlug(slug) || isGotoConnectIdSlug(slug);

  function formatHip(raw = "") {
    // mantém só dígitos e corta em 10 (8 + 2)
    const only = String(raw).replace(/\D+/g, "").slice(0, 10);
    if (only.length <= 8) return only;               // ainda digitando a parte inicial
    return `${only.slice(0,8)}-${only.slice(8)}`;    // 8 + '-' + 2
  }

  function formatGoto(raw = "") {
    // mantém hexa, normaliza minúsculo e corta em 32
    const hex = String(raw).toLowerCase().replace(/[^0-9a-f]+/g, "").slice(0, 32);
    // monta nos grupos 8-4-4-4-12 conforme disponível
    const g1 = hex.slice(0, 8);
    const g2 = hex.slice(8, 12);
    const g3 = hex.slice(12, 16);
    const g4 = hex.slice(16, 20);
    const g5 = hex.slice(20, 32);
    return [g1, g2, g3, g4, g5].filter(Boolean).join("-"); // junta só o que existir
  }

  const [invalidCampos, setInvalidCampos] = React.useState({});
  const [imagemAuxiliarSrc, setImagemAuxiliarSrc] = React.useState(null);

  function validateBySlug(slug, v) {
    if (isHiplatformProtocoloSlug(slug)) return v === "" || HIP_RE.test(v);
    if (isGotoConnectIdSlug(slug))       return v === "" || UUID_RE.test(v);
    return true;
  }

  // Agrupa campos por sessão
  const agrupado = campos.reduce((acc, campo) => {
    const sessao = campo.sessao || '0_sem_sessao';
    if (!acc[sessao]) acc[sessao] = [];
    acc[sessao].push(campo);
    return acc;
  }, {});

  const getExtraValor = React.useCallback((slug) => {
    const item = (camposExtraCadastrados || []).find(
      x => (x?.campo_slug || '').toLowerCase() === String(slug).toLowerCase()
    );
    return (item?.valor ?? '').toString().trim();
  }, [camposExtraCadastrados]);

  const splitNomeCompleto = React.useCallback((fullName) => {
    const clean = (fullName || '').trim().replace(/\s+/g, ' ');
    if (!clean) return { nome: '', sobrenome: '' };
    const partes = clean.split(' ');
    const nome = partes[0] || '';
    const sobrenome = partes.slice(1).join(' ');
    return { nome, sobrenome };
  }, []);

  // Função para avaliar hide/show-on
  function deveEsconder(campo) {

    if (campo.doenca_id && campo.doenca_id !== doencaId) {
        return true; // Não renderiza o campo
    }

    let hide = false;
    const mask = campo.mascara || '';
    // hide=tipo|...
    if (mask.includes('hide=')) {
      const hideVals = mask.split('hide=')[1].split('|');
      if (hideVals.includes(dadosUsuario.tipo_usuario)) hide = true;
    }
    // show-on[condição]
    if (!hide && mask.includes('show-on[')) {
      const m = mask.match(/show-on\[(.*?)\]/);
      if (m) {
        const cond = m[1];
        const opMatch = cond.match(/(<=|>=|<|>|=)/);
        if (opMatch) {
          const op = opMatch[0];
          const [slug, valExp] = cond.split(op);
          const related = campos.find(c => c.slug === slug.trim());
          if (related) {
            const atual = valoresCampos?.[related.id] || '';
            const expNum = parseFloat(valExp.trim());
            let ok = false;
            const numAtual = parseFloat(atual);
            switch(op) {
              case '<': ok = numAtual < expNum; break;
              case '>': ok = numAtual > expNum; break;
              case '<=': ok = numAtual <= expNum; break;
              case '>=': ok = numAtual >= expNum; break;
              case '=': {
                // suportar lista de valores separados por '|'
                const expected = valExp.split('|').map(v => v.trim());
                const atualStr = (valoresCampos?.[related.id] || '').toString();
                ok = expected.includes(atualStr);
                break;
              }
            }
            if (!ok) hide = true;
          }
        }
      }
    }

    // prefill[condição]
    if (!hide && mask.includes('prefill[')) {
      const m = mask.match(/data_atual\[(.*?)\]/);

    }

    return hide;
  }

  function getImagemAuxiliar(mascara) {
    const m = (mascara || '').match(/imagem-auxiliar\[([^\]]+)\]/);
    return m ? m[1].trim() : null;
  }

  // mapeia nome da máscara para pattern do IMask
  function getMaskPattern(mascara) {
    const m = mascara.match(/mask\[(.+)\]/);
    if (!m) return null;
    switch (m[1]) {
      case 'cpf': return '000.000.000-00';
      case 'telefone': return '(00) 00000-0000';
      case 'cep': return '00000-000';
      default: return null;
    }
  }

  function todayISO() {
    return formatDateToLocalYMD(new Date()); // YYYY-MM-DD
  }

  function formatDateToLocalYMD(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  function parseLocalYMDToDate(value) {
    if (!value || typeof value !== 'string') return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (!m) return null;
    const [, y, mo, d] = m;
    const dt = new Date(Number(y), Number(mo) - 1, Number(d));
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  function getDateLimits(mascara = '') {
    let min = null, max = null;

    // atalhos
    if (/\bno-future\b/i.test(mascara)) max = todayISO();
    if (/\bno-past\b/i.test(mascara))   min = todayISO();

    // explícitos
    const mMax = mascara.match(/date-max\[(today|\d{4}-\d{2}-\d{2})\]/i);
    const mMin = mascara.match(/date-min\[(today|\d{4}-\d{2}-\d{2})\]/i);

    const resolve = (v) => (String(v).toLowerCase() === 'today' ? todayISO() : v);

    if (mMax) max = resolve(mMax[1]);
    if (mMin) min = resolve(mMin[1]);

    return { min, max };
  }

  // compara/clampa strings no formato YYYY-MM-DD (ordem lexicográfica funciona)
  function clampDate(val, { min, max }) {
    let v = val || '';
    if (min && v < min) v = min;
    if (max && v > max) v = max;
    return v;
  }

  React.useEffect(() => {
    if (!Array.isArray(campos) || campos.length === 0) return;

    console.log("dados_beneficiario - ", dados_beneficiario);

    campos.forEach((campo) => {
      const mask = campo.mascara || '';
      if (!mask.includes('prefill[')) return;

      // só preenche se ainda não houver valor
      const temValor =
        valoresCampos?.[campo.id] !== undefined &&
        valoresCampos?.[campo.id] !== null &&
        valoresCampos?.[campo.id] !== '';

      if (temValor) return;

      // Data Atual
      if (/\bprefill\[data_atual\]/.test(mask)) {
        const agora = new Date();
        const valor = (campo.tipo === 'data') 
            ? formatDateToLocalYMD(agora)
            : getDataAtual();
        
        onChangeCampo(campo.id, valor);
      }
    
      // País Brasil
      if (/\bprefill\[pais-brasil\]/.test(mask)) {
        onChangeCampo(campo.id, 'Brasil');
      }
    
    //Usuario
    //Nome do Usuario
      if (/\bprefill\[pessoa_preencheu\]/.test(mask)) {
        if(dadosUsuario.name && dadosUsuario.last_name) {
          onChangeCampo(campo.id, dadosUsuario.name + ' ' + dadosUsuario.last_name);
        }
      }

      if (/\bprefill\[email_usuario\]/.test(mask)) {
        onChangeCampo(campo.id, dadosUsuario.email);
      }

      if (/\bprefill\[telefone_formulario\]/.test(mask)) {
        onChangeCampo(campo.id, programaDados.canais_contato  );
      }

    //Medico
      if (/\bprefill\[medico_prescritor\]/.test(mask)) {
        if(dadosMedico.nome) {
          onChangeCampo(campo.id, dadosMedico.nome);
        }
      }

      if (/\bprefill\[medico_prescritor_nome\]/.test(mask)) {
        if (dadosMedico?.nome) {
          const { nome } = splitNomeCompleto(dadosMedico.nome);
          if (nome) onChangeCampo(campo.id, nome);
        }
      }

      if (/\bprefill\[medico_prescritor_sobrenome\]/.test(mask)) {
        if (dadosMedico?.nome) {
          const { sobrenome } = splitNomeCompleto(dadosMedico.nome);
          if (sobrenome) onChangeCampo(campo.id, sobrenome);
        }
      }

      if (/\bprefill\[medico_telefone\]/.test(mask)) {
        if(dadosMedico.telefone) {
          onChangeCampo(campo.id, dadosMedico.telefone);
        }
      }

      if (/\bprefill\[medico_endereco\]/.test(mask)) {
        if(dadosMedico.endereco) {
          onChangeCampo(campo.id, dadosMedico.endereco);
        }
      }

      if (/\bprefill\[medico_cidade\]/.test(mask)) {
        if(dadosMedico.cidade) {
          onChangeCampo(campo.id, dadosMedico.cidade);
        }
      }

      if (/\bprefill\[medico_estado\]/.test(mask)) {
        if(dadosMedico.uf) {
          onChangeCampo(campo.id, dadosMedico.uf);
        }
      }

      if (/\bprefill\[medico_cep\]/.test(mask)) {
        if(dadosMedico.cep) {
          onChangeCampo(campo.id, dadosMedico.cep);
        }
      }

      if (/\bprefill\[medico_email\]/.test(mask)) {
        if(dadosMedico.email) {
          onChangeCampo(campo.id, dadosMedico.email);
        }
      }

    //Beneficiario
    /* //DADOS DO BENEFICIARIO */

      // Nome do Beneficiario
      if (/\bprefill\[iniciais_paciente\]/.test(mask)) {
        if (dados_beneficiario?.nome) {
          // Quebra o nome em partes (separa por espaço)
          const partes = dados_beneficiario.nome.trim().split(/\s+/);

          // Pega a primeira letra de cada parte e adiciona ponto
          const iniciais = partes
            .filter(p => !['da','de','do','dos','das'].includes(p.toLowerCase()))
            .map(p => p[0].toUpperCase() + ".")
            .join(" ");

          onChangeCampo(campo.id, iniciais);
        }
      }

      // Data de Nascimento do Beneficiario
      /*if (/\bprefill\[data_nascimento\]/.test(mask)) {
        onChangeCampo(campo.id, dados_beneficiario.data_nascimento);
      }

      // Sexo do Beneficiario
      if (/\bprefill\[sexo_beneficiario\]/.test(mask)) {
        onChangeCampo(campo.id, dados_beneficiario.sexo);
      }*/
      

      // 2.1) Padrão genérico: prefill[extra:slug]
      {
        const mExtra = mask.match(/prefill\[\s*extra:([a-z0-9_\-]+)\s*\]/i);
        if (mExtra) {
          const slug = mExtra[1];
          const item = getExtraItem(slug);

          // presets de repetidor (casos especiais)
          const presets = {
            // Dose: valor numérico + unidade -> "10 ml; 5 ml;"
            'dose-prescrita': {
              slugs: { a: 'dose_prescrita_valor', b: 'dose_prescrita_unidade' },
              combine: ({ a, b }) => `${a} ${b}`,
            },

            // Apresentação: apresentação + quantidade -> "Frasco 10 mg x 1; Frasco 20 mg x 1;"
            'apresentacao_repeater': {
              slugs: { a: 'apresentacao_apresentacao', b: 'apresentacao_quantidade' },
              combine: ({ a, b }) => `${a} x ${b}`,
            },
          };

          if (item) {
            const cfg = presets[slug];
            if (cfg) {
              const texto = joinRepeaterPairs(item, cfg.slugs, cfg.combine, '; ', true);
              if (texto) onChangeCampo(campo.id, texto);
            } else {
              const v = (item.valor || '').toString().trim();
              if (v && v.toLowerCase() !== 'não informado') onChangeCampo(campo.id, v);
            }
          }
        }
      }

      {
        const mBenef = mask.match(/prefill\[\s*benef:([a-z0-9_\-]+)\s*\]/i);
        if (mBenef) {
          const slug = mBenef[1];
          const v = getBenefValor(slug);
          if (v) onChangeCampo(campo.id, v);
        }
      }

    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campos]); // não depende de onChangeCampo para evitar re-render loop

  // Seleciona automaticamente o "produtos_programa" a partir de produtoNome
  React.useEffect(() => {
    if (!produtoNome) return;

    const valorNormalizado = produtoNome.split('-')[0].trim(); // "Crysvita - 20mg" -> "Crysvita"

    // Ache todos os campos cuja máscara contenha "produtos_programa"
    const camposProduto = (campos || []).filter(
      c => (c.mascara || '').includes('produtos_programa')
    );
    if (camposProduto.length === 0) return;

    // Não sobrescreve se já houver valor
    camposProduto.forEach(campo => {
      const atual = valoresCampos?.[campo.id];
      if (atual === undefined || atual === null || String(atual).trim() === '') {
        onChangeCampo(campo.id, valorNormalizado);
      }
    });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campos, produtoNome]);

  // helpers no componente
  const getExtraItem = React.useCallback((slug) => {
    return (camposExtraCadastrados || []).find(
      x => (x?.campo_slug || '').toLowerCase() === String(slug).toLowerCase()
    );
  }, [camposExtraCadastrados]);

  // Junta pares de repetidor sem assumir ordem (A e B podem vir em qualquer sequência)
  const joinRepeaterPairs = (
    item,
    slugs = { a: '', b: '' },                       // { a: 'slugA', b: 'slugB' }
    combine = ({ a, b }) => `${a} ${b}`,            // como combinar o par
    sep = '; ',                                     // separador entre pares
    addTrailing = true                               // adiciona ";" final?
  ) => {
    if (!item || !Array.isArray(item.repetidor_list)) return '';
    const { a: slugA, b: slugB } = slugs;

    const pairs = [];
    let buf = { a: '', b: '' };

    for (const r of item.repetidor_list) {
      const s = r?.subcampo__slug;
      if (!s) continue;
      const val = (r.valor || '').toString().trim();

      if (s === slugA) {
        // se já tínhamos A e B, fecha e recomeça
        if (buf.a && buf.b) {
          pairs.push(combine(buf));
          buf = { a: '', b: '' };
        }
        buf.a = val;

        // se já há B, fecha
        if (buf.b) {
          pairs.push(combine(buf));
          buf = { a: '', b: '' };
        }
      } else if (s === slugB) {
        if (buf.a && buf.b) {
          pairs.push(combine(buf));
          buf = { a: '', b: '' };
        }
        buf.b = val;

        if (buf.a) {
          pairs.push(combine(buf));
          buf = { a: '', b: '' };
        }
      }
    }

    // ignora par incompleto
    const joined = pairs.filter(Boolean).join(sep);
    return joined + (joined && addTrailing ? ';' : '');
  };

  const getBenefValor = React.useCallback((slug) => {
    const lista = dados_beneficiario?.extra_valores_list || [];
    if (!Array.isArray(lista)) return '';

    const item = lista.find(
      x => (x?.campo_slug || '').toLowerCase() === String(slug).toLowerCase()
    );

    const val = (item?.valor ?? '').toString().trim();
    return val && val.toLowerCase() !== 'não informado' ? val : '';
  }, [dados_beneficiario]);

  function avaliaCondicao(cond, campos, valoresCampos) {
    const opMatch = cond.match(/(<=|>=|<|>|=)/);
    if (!opMatch) return false;

    const op = opMatch[0];
    const [slugRaw, valRaw] = cond.split(op);
    const slug = slugRaw.trim();
    const related = campos.find(c => c.slug === slug);
    if (!related) return false;

    const atual = (valoresCampos?.[related.id] ?? '').toString().trim();
    const expectedRaw = (valRaw ?? '').trim();

    if (op === '=') {
      // suporte a lista: v1|v2|v3
      const expected = expectedRaw.split('|').map(v => v.trim());
      return expected.includes(atual);
    }

    // comparações numéricas
    const numAtual = parseFloat(atual);
    const numExp = parseFloat(expectedRaw);
    if (Number.isNaN(numAtual) || Number.isNaN(numExp)) return false;

    switch (op) {
      case '<':  return numAtual <  numExp;
      case '<=': return numAtual <= numExp;
      case '>':  return numAtual >  numExp;
      case '>=': return numAtual >= numExp;
      default:   return false;
    }
  }

  function isObrigatorio(campo, campos, valoresCampos, opcaoModalRelato) {
    // regra padrão existente
    let obrig = !!(campo.obrigatorio && opcaoModalRelato !== 'novo_relato');

    const mask = campo.mascara || '';

    // required-on[slug=...]
    const m = mask.match(/required-on\[(.*?)\]/);
    if (m) {
      const cond = m[1];
      const ok = avaliaCondicao(cond, campos, valoresCampos);
      // Quando existir required-on, a regra condicional **dita** o obrigatório
      obrig = ok;
    }

    return obrig;
  }

  return (
    <>
      {Object.entries(agrupado).map(([sessao, lista]) => (
        <div className="row-list" key={sessao}>
          {sessao !== '0_sem_sessao' && (
            <h6>
              <strong>
                {tiposSessoesCampos[sessao] ?? sessao}
              </strong>
            </h6>
          )}
          <Row>
            {lista.map(campo => {
              if (deveEsconder(campo)) return null;
              const obrig = isObrigatorio(campo, campos, valoresCampos, opcaoModalRelato);
              const maskPattern = getMaskPattern(campo.mascara || '');

              // tipo "informacao"
              if (campo.tipo === 'informacao') {
                return (
                  <Col sm={12} className="mb-3" key={campo.id}>
                    <div dangerouslySetInnerHTML={{ __html: campo.descricao }} />
                  </Col>
                );
              }

              // tipo "gotoconnect"
              if (campo.tipo === 'gotoconnect') {
                const {
                  goToClientId,
                  loadingGoto,
                  goToErroMensagem,
                  goToAccessToken,
                  goToLinhasAtendente = [],
                  goToLinhaLigar,
                  onChangeLinha,
                  ligarParaPaciente,
                  loadingLigacao,
                  goToLigacaoRealizada,
                  beneficiarioCelular
                } = gotoConfig;
                return (
                  <Col sm={12} className="mb-3" key={campo.id}>
                    { goToClientId ? (
                      loadingGoto ? (
                        <h4 className="text-center mt-3"><FaClock /> Carregando...</h4>
                      ) : goToErroMensagem ? (
                        <h4 className="text-center mt-3"><FaExclamationTriangle /> {goToErroMensagem}</h4>
                      ) : goToAccessToken ? (
                        goToLinhasAtendente.length > 0 && (
                          <>
                            <Row>
                              <Col sm={6} xs={12} className="mb-3">
                                <Form.Group>
                                  <Form.Label>Linhas disponíveis</Form.Label>
                                  <Form.Control
                                    as="select"
                                    name="goToLinhaLigar"
                                    onChange={onChangeLinha}
                                    value={goToLinhaLigar}
                                  >
                                    {goToLinhasAtendente.map((l, i) => (
                                      <option key={i} value={l.id}>
                                        {l.name} (Número: {l.number})
                                      </option>
                                    ))}
                                  </Form.Control>
                                </Form.Group>
                              </Col>
                              <Col sm={6} xs={12} className="mb-3">
                                <Button
                                  variant="success"
                                  size="sm"
                                  disabled={loadingLigacao}
                                  onClick={() => ligarParaPaciente(campo.id)}
                                >
                                  {loadingLigacao ? (
                                    <span className="spinner-border spinner-border-sm" />
                                  ) : (
                                    <><FaPhone className="me-2" /> Ligar para {beneficiarioCelular}</>
                                  )}
                                </Button>
                              </Col>
                            </Row>
                            {goToLigacaoRealizada && (
                              <Alert variant="success">
                                <FaCheckCircle className="me-2" />
                                <strong>Ligação iniciada! Acesse app.goto.com para prosseguir.</strong>
                              </Alert>
                            )}
                          </>
                        )
                      ) : (
                        <h4 className="text-center mt-3"><FaClock /> Aguardando login na Goto Connect...</h4>
                      )
                    ) : (
                      <h4 className="text-center mt-3"><FaExclamation /> O seu cliente não possui integração com o Goto Connect.</h4>
                    )}
                  </Col>
                );
              }

              const valorCampoAtual = valoresCampos?.[campo.id] || '';
              const campoMaxLength = maxLengthOverrides?.[campo.id] || null;
              const slug = (campo.slug || '').toLowerCase();

              // Colunas padrão
              return (
                <React.Fragment key={campo.id}>
                  {/* texto, area_texto, data, selecao, radio, checkbox */}
                  {['texto','area_texto','data','selecao','radio','checkbox'].includes(campo.tipo) && (
                    <Col sm={campo.tipo==='area_texto'?12:6} className={campo.tipo==='checkbox'?'mb-1':'mb-3'}>
                      <Form.Group>
                        {(campo.tipo !== 'checkbox' || campo.mascara?.includes('multiple-select')) && <Form.Label>{campo.nome}{obrig && <span className="text-danger"> *</span>}</Form.Label>}

                        {maskPattern ? (
                          <IMaskInput
                            mask={maskPattern}
                            value={valorCampoAtual}
                            onAccept={(val) => onChangeCampo(campo.id, val)}
                            unmask={false}
                            overwrite
                            className="form-control"
                            required={obrig}
                          />
                        ) : isMaskedBySlug(slug) ? (
                          <>
                            <div className="d-flex align-items-center">
                              <Form.Control
                                  type="text"
                                  autoComplete="off"
                                  className="form-control"
                                  required={obrig}
                                  aria-invalid={!!invalidCampos[campo.id]}
                                  value={valorCampoAtual}
                                  onChange={(e) => {
                                    const raw = e.target.value ?? "";
                                    const formatted = isHiplatformProtocoloSlug(slug)
                                      ? formatHip(raw)
                                      : formatGoto(raw);
                                    onChangeCampo(campo.id, formatted);
                                    if (invalidCampos[campo.id]) {
                                      setInvalidCampos(prev => ({ ...prev, [campo.id]: false }));
                                    }
                                  }}
                                  onPaste={(e) => {
                                    const text = e.clipboardData?.getData('text') ?? '';
                                    const formatted = isHiplatformProtocoloSlug(slug)
                                      ? formatHip(text)
                                      : formatGoto(text);
                                    e.preventDefault();
                                    onChangeCampo(campo.id, formatted);
                                    if (invalidCampos[campo.id]) {
                                      setInvalidCampos(prev => ({ ...prev, [campo.id]: false }));
                                    }
                                  }}
                                  onBlur={(e) => {
                                    const v = (e.target.value || '').trim();
                                    const ok = validateBySlug(slug, v);
                                    if (!ok) {
                                      onChangeCampo(campo.id, "");
                                      setInvalidCampos(prev => ({ ...prev, [campo.id]: true }));
                                    } else {
                                      setInvalidCampos(prev => ({ ...prev, [campo.id]: false }));
                                    }
                                  }}
                                  maxLength={isHiplatformProtocoloSlug(slug) ? 11 : 36}
                                  inputMode={isHiplatformProtocoloSlug(slug) ? 'numeric' : 'text'}
                                />
                              {
                              isGotoConnectIdSlug(slug) &&
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => abrirModalHistoricoLigacoes(campo.id)}
                                >
                                  Consultar
                                </Button>
                              } 
                            </div>
                            {invalidCampos[campo.id] && (
                              <Form.Text className="text-danger text-small">
                                Valor inválido
                              </Form.Text>
                            )}
                          </>
                        ) : campo.mascara === 'produtos_programa' ? (
                          (() => {
                            const isMultiple = campo.mascara?.includes('multiple-select');

                            // valor atual normalizado (array quando múltiplo)
                            const atual = valoresCampos?.[campo.id] ?? (isMultiple ? '' : '');
                            const valueControlled = isMultiple
                              ? (Array.isArray(atual) ? atual : (atual ? atual.split(',').map(s => s.trim()) : []))
                              : (typeof atual === 'string' ? atual : '');

                            return (
                              <>
                                <Form.Control
                                  as="select"
                                  multiple={isMultiple}
                                  required={obrig}
                                  value={valueControlled}
                                  onChange={e => {
                                    if (isMultiple) {
                                      const sel = Array.from(e.target.selectedOptions).map(o => o.value);
                                      onChangeCampo(campo.id, sel.join(', '));
                                    } else {
                                      onChangeCampo(campo.id, e.target.value);
                                    }
                                  }}
                                >
                                  {!isMultiple && (
                                    <option value="">
                                      Selecione...
                                    </option>
                                  )}
                                  {produtosOpcoes.map((opt, i) => (
                                    <option key={i} value={opt.value}>{opt.label}</option>
                                  ))}
                                </Form.Control>
                              </>
                            );
                          })()
                        ) : campo.tipo === 'selecao' ? (
                          /* select simples ou múltiplo */
                          (() => {
                            const isMultiple = campo.mascara?.includes('multiple-select');
                            return (
                              <Form.Control
                                as="select"
                                multiple={isMultiple}
                                required={obrig}
                                value={isMultiple ? valorCampoAtual.split(', ') : valorCampoAtual}
                                onChange={e => {
                                  if (isMultiple) {
                                    const sel = Array.from(e.target.selectedOptions).map(o => o.value);
                                    onChangeCampo(campo.id, sel.join(', '));
                                  } else {
                                    onChangeCampo(campo.id, e.target.value);
                                  }
                                }}
                              >
                                {!isMultiple && <option value="">Selecione...</option>}
                                {campo.opcoes.split('\n').map((o, i) => {
                                  const [v, l] = o.split(':');
                                  return (
                                    <option key={i} value={v}>
                                      {l}
                                    </option>
                                  );
                                })}
                              </Form.Control>
                            );
                          })()
                        ) : campo.tipo === 'radio' ? (
                          <div role="radiogroup" aria-labelledby={`lbl_${campo.id}`}>
                            {campo.opcoes.split('\n').map((o, i) => {
                              const [v, l] = o.split(':');
                              return (
                                <Form.Check
                                  inline
                                  type="radio"
                                  id={`radio_${campo.id}_${i}`}
                                  key={i}
                                  name={`radio_${campo.id}`}
                                  label={l}
                                  value={v}
                                  checked={valoresCampos?.[campo.id] === v}
                                  onClick={() => {
                                    if (valoresCampos?.[campo.id] === v) {
                                      onChangeCampo(campo.id, '');
                                    }
                                  }}
                                  onChange={e => onChangeCampo(campo.id, e.target.value)}
                                  required={obrig && i === 0} 
                                />
                              );
                            })}
                          </div>
                        ) : campo.tipo === 'checkbox' ? (
                          campo.mascara?.includes('multiple-select') ? (
                            <div className="mb-3">
                              {(() => {
                                const atuais = (valoresCampos?.[campo.id] || '')
                                  .split(', ')
                                  .filter(Boolean);

                                return (
                                  <>
                                    <input
                                      type="text"
                                      className="visually-hidden"
                                      tabIndex={-1}
                                      autoComplete="off"
                                      value={atuais.length ? 'ok' : ''} 
                                      onChange={() => {}}
                                      required={obrig}
                                    />

                                    {campo.opcoes.split('\n').map((o, i) => {
                                      const [valor, label] = o.split(':');
                                      const checked = atuais.includes(valor);

                                      return (
                                        <Form.Check
                                          key={i}
                                          type="checkbox"
                                          id={`chk_${campo.id}_${i}`}
                                          label={label}
                                          value={valor}
                                          checked={checked}
                                          onChange={e => {
                                            const selecionados = new Set(atuais);
                                            if (e.target.checked) {
                                              selecionados.add(valor);
                                            } else {
                                              selecionados.delete(valor);
                                            }
                                            onChangeCampo(campo.id, Array.from(selecionados).join(', '));
                                          }}
                                        />
                                      );
                                    })}
                                  </>
                                );
                              })()}
                            </div>
                          ) : (
                            // seu checkbox simples original
                            <Form.Check
                              type="checkbox"
                              id={`check_${campo.slug}`}
                              label={campo.nome}
                              checked={!!valoresCampos?.[campo.id]}
                              onChange={e => onChangeCampoCheckSim(campo.id, e)}
                              required={obrig}
                            />
                          )
                        ) : (campo.tipo === 'data' || campo.tipo === 'date') ? (
                            
                            (campo.mascara || '').includes('format-mmm-en') ? (
                                <InputGroup>
                                    <DatePicker
                                        selected={
                                            valoresCampos?.[campo.id]
                                                ? parseLocalYMDToDate(valoresCampos?.[campo.id]) 
                                                : null
                                        }
                                        onChange={(date) => {
                                            const formatted = date ? formatDateToLocalYMD(date) : "";
                                            onChangeCampo(campo.id, formatted);
                                        }}
                                        dateFormat="dd/MMM/yyyy"
                                        placeholderText="Select date"
                                        className="form-control" 
                                        calendarClassName="shadow-sm border"
                                        required={obrig}
                                        showYearDropdown
                                        scrollableYearDropdown
                                        yearDropdownItemNumber={100}
                                    />
                                    <InputGroup.Text className="bg-light">
                                        <i className="bi bi-calendar-event text-secondary"></i>
                                    </InputGroup.Text>
                                </InputGroup>
                            ) : (
                                <Form.Control
                                    type="date"
                                    required={obrig}
                                    value={valoresCampos?.[campo.id]||''}
                                    onChange={e => onChangeCampo(campo.id, e.target.value)}
                                />
                            )

                        ) : (
                          // texto, data ou area_texto
                          (() => {
                            const isDate = campo.tipo === 'data';
                            const limits = isDate ? getDateLimits(campo.mascara || '') : {};

                            const input = (
                                <Form.Control
                                  as={campo.tipo === 'area_texto' ? 'textarea' : undefined}
                                  type={isDate ? 'date' : 'text'}
                                  required={obrig}
                                  value={valoresCampos?.[campo.id] || ''}
                                  maxLength={campoMaxLength || undefined}
                                  min={isDate ? limits.min || undefined : undefined}
                                  max={isDate ? limits.max || undefined : undefined}
                                  onChange={e => {
                                    const v = e.target.value;
                                    onChangeCampo(
                                      campo.id,
                                      isDate ? clampDate(v, limits) : v
                                    );
                                  }}
                                  className={campo.tipo === 'area_texto' ? 'custom-textarea2' : undefined}
                                />
                            );

                            // para o campo específico do GotoConnect, adiciona o botão de consulta
                            if (isGotoConnectIdSlug(campo.slug || '')) {
                              return (
                                <div className="d-flex align-items-center">
                                  {input}
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => abrirModalHistoricoLigacoes(campo.id)}
                                  >
                                    Consultar
                                  </Button>
                                </div>
                              );
                            }

                            // para os demais campos, só o input
                            return input;
                          })()
                        )}

                        {/* outro adicional */}
                        {campo.tipo !== 'checkbox' && ['outro', 'outros'].includes(valoresCampos?.[campo.id]?.toLowerCase()) && (
                          <Form.Control
                            type="text"
                            placeholder="Descreva..."
                            className="mt-1"
                            value={camposOutro[campo.id]||''}
                            onChange={e => onChangeOutro(campo.id, e.target.value)}
                            required={obrig}
                            maxLength={150}
                          />
                        )}

                        {campo.descricao && (
                          <Form.Text className="text-muted" style={{ fontSize: '0.9em' }}>
                            {campo.descricao}
                          </Form.Text>
                        )}
                        {campoMaxLength && (
                          <Form.Text className="text-muted d-block" style={{ fontSize: '0.8em' }}>
                            {`${String(valoresCampos?.[campo.id] || '').length}/${campoMaxLength} caracteres`}
                          </Form.Text>
                        )}
                        {(() => {
                          const imgSrc = getImagemAuxiliar(campo.mascara);
                          return imgSrc ? (
                            <div className="mt-1">
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0"
                                onClick={() => setImagemAuxiliarSrc(imgSrc)}
                              >
                                <i className="bi bi-image me-1" />
                                Ver imagem auxiliar
                              </Button>
                            </div>
                          ) : null;
                        })()}
                      </Form.Group>
                    </Col>
                  )}

                  {/* arquivo */}
                  {campo.tipo === 'arquivo' && (
                    <Col sm={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{campo.nome}{obrig && <span className="text-danger"> *</span>}</Form.Label>
                        <Form.Control
                          type="file"
                          required={obrig}
                          onChange={e => onChangeCampo(campo.id, e)}
                        />
                        {campo.descricao && (
                          <Form.Text className="text-muted">{campo.descricao}</Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  )}

                  {/* switchs cancela_atendimento e voltar_etapa */}
                  {['cancela_atendimento','voltar_etapa'].includes(campo.tipo) && (
                    <Col sm={6} className="mb-3">
                      <Form.Group>
                        <Form.Check
                          type="switch"
                          name={campo.tipo}
                          id={campo.tipo}
                          label={campo.nome}
                          value="1"
                          required={obrig}
                          checked={!!valoresCampos?.[campo.id]}
                          onChange={e => onChangeCampo(campo.id, e)}
                        />
                        {campo.descricao && (
                          <Form.Text className="text-muted">{campo.descricao}</Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  )}
                </React.Fragment>
              );
            })}
          </Row>
        </div>
      ))}

      <Modal
        show={!!imagemAuxiliarSrc}
        onHide={() => setImagemAuxiliarSrc(null)}
        size="lg"
        dialogClassName="modal-level-2"
        backdropClassName="backdrop-level-2"
      >
        <Modal.Header closeButton />
        <Modal.Body className="text-center p-2">
          <img
            src={imagemAuxiliarSrc}
            alt="Imagem auxiliar"
            className="img-fluid"
          />
        </Modal.Body>
      </Modal>
    </>
  );
}
